import React, { Dispatch, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DialogHeader from '../_parts/DialogHeader';
import { useDispatch, useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../../redux/store';
import DialogFrame from '../_parts/DialogFrame';

import { useParams } from 'react-router-dom';

import { DialogContent } from '@mui/material';
import { ContentLayout } from '../_parts/ContentLayout';
import Documents from '../_parts/Documents';
import { removeProjectDocumentsInfoItem } from '../../../../redux/project/documents/sliceProjectDocuments';
import {
    closeCreateReportFromTemplateDialog,
    CreateReportFromTemplateDialogEnum,
    getReportTemplatesForCreateReport,
    selectReportTemplate,
    SelectReportVersionEnum,
} from '../../../../redux/project/documents/sliceProjectReportTemplate';
import CreateReportContent from './_parts/CreateReportContent';
import CreateReportDialogActions from './_parts/CreateReportDialogActions';
import { IReportTemplate } from '../../../../types/reportTemplate';
import { getUserEmailList } from '../../../../redux/project/sliceProjectUserList';

const initialMainState: ICreateReportFromTemplateMainState = {
    selectedDocumentTypes: [],
    selectedVersionToInclude: SelectReportVersionEnum.SelectedDocuments,
    sendTo: [],
};

const initialErrorState: ICreateReportFromTemplateErrorState = {
    selectedDocumentTypes: false,
    sendTo: false,
};

export default () => {
    const { t } = useTranslation('translations', { keyPrefix: 'createReportDialog' });
    const dispatch: Dispatch<unknown> = useDispatch();

    const { replicaId } = useParams();

    const { data: documents, loading: loadingInfoDocs } = useSelector(
        (_store: IAppReduxStore) => _store.projectDocuments.info,
    );

    const {
        isDialogOpen,
        loading: loadingEmailTemplates,
        selectedTemplate,
        docTypes,
        typeOfDialog,
    } = useSelector((_store: IAppReduxStore) => _store.projectDocumentsReportFromTemplate);

    const projectDocTypes = useSelector((store: IAppReduxStore) => store.projectDocTypes.data);

    useEffect(() => {
        if (isDialogOpen) {
            dispatch(getReportTemplatesForCreateReport({ replicaId, typeOfDialog }));
            dispatch(getUserEmailList({ replicaId }));
        }
    }, [isDialogOpen]);

    useEffect(() => {
        selectedTemplate && handleChangeTemplate(selectedTemplate);
    }, [selectedTemplate]);

    const [mainState, setMainState] =
        useState<ICreateReportFromTemplateMainState>(initialMainState);
    const [showErrorState, setShowErrorState] =
        useState<ICreateReportFromTemplateErrorState>(initialErrorState);

    const docTypeItems = projectDocTypes.filter(x => docTypes?.includes(x.name)).map(x => x.name);

    const handleChangeTemplate = (template: IReportTemplate) => {
        dispatch(selectReportTemplate(template));

        setMainState(prevState => ({
            ...prevState,
            selectedVersionToInclude: template.DocumentChoice,
            selectedDocumentTypes: docTypeItems,
        }));
        setShowErrorState(initialErrorState);
    };

    const handleChange = ({ field, value }: { field: string; value: string }) => {
        setShowErrorState({ ...showErrorState, [field]: false });
        setMainState(prevState => ({
            ...prevState,
            [field]: value,
        }));
    };

    const resetState = () => {
        setShowErrorState(initialErrorState);
        setMainState({ ...initialMainState });
    };

    const onClose = () => {
        resetState();
        dispatch(closeCreateReportFromTemplateDialog());
    };

    const removeDocument = (universalId: string) => {
        dispatch(removeProjectDocumentsInfoItem({ universalId }));
    };

    return (
        <DialogFrame open={isDialogOpen}>
            <DialogHeader onClose={onClose} dialogTitle={t('dialogTitle')} />
            <DialogContent sx={{ pt: 0, overflowX: 'hidden' }}>
                <ContentLayout
                    loading={loadingInfoDocs || loadingEmailTemplates}
                    documentSelection={
                        typeOfDialog === CreateReportFromTemplateDialogEnum.SelectedDocuments && (
                            <Documents removeDocument={removeDocument} documents={documents} />
                        )
                    }
                    main={
                        <CreateReportContent
                            mainState={mainState}
                            handleChange={handleChange}
                            showErrorState={showErrorState}
                            handleChangeTemplate={handleChangeTemplate}
                        />
                    }
                />
            </DialogContent>

            <CreateReportDialogActions
                setShowErrorState={setShowErrorState}
                mainState={mainState}
                onClose={onClose}
            />
        </DialogFrame>
    );
};

export interface ICreateReportFromTemplateMainState {
    selectedDocumentTypes: string[];
    selectedVersionToInclude: SelectReportVersionEnum;
    sendTo?: string[];
}

export interface ICreateReportFromTemplateErrorState {
    selectedDocumentTypes: boolean;
    sendTo: boolean;
}
